import axios from "axios";
import React, { useState, useEffect } from "react";

import { ReactComponent as Pen } from "../../../assets/icons/pen.svg";
import { ReactComponent as Undo } from "../../../assets/icons/undo.svg";
import { registrationCategory } from "../../../constants/register.constants";
import APIVariables from "../../../services/api-variables";
import PatientsDetails from "../../common/PatientDetails/PatientDetails";
import StyledProfile from "./StyledProfile";
import {
  displayPhoneNumberWithSpace,
  formatPhoneNumber,
  handleCatch,
  isEmptyObject,
  validateEmail,
  validatePhoneNumber,
  verifyUserType,
  verifyUserTypeConnectedRegistrar,
} from "../../../shared/helpers";
import LocalStorage from "../../../shared/localStorage";
import Loader from "../../common/Loader/Loader";
import Footer from "../../common/Footer/Footer";

const Profile = ({ setLoggedInInitials }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [patientsStatistics, setPatientsStatistics] = useState({});

  const getLocalStorage = localStorage.getItem(LocalStorage.USER_IRELAND);
  const parseLocalStorage = JSON.parse(getLocalStorage);

  const isPrescriber = verifyUserType(registrationCategory.PRESCRIBER);
  const isPharmacy = verifyUserType(registrationCategory.PHARMACY);
  const isConnectedRegistrar = verifyUserTypeConnectedRegistrar(
    registrationCategory.CONNECTED_REGISTRAR
  );
  const isConnectedNurse = verifyUserTypeConnectedRegistrar(
    registrationCategory.CONNECTED_NURSE
  );

  const [profileDetails, setProfileDetails] = useState({});
  const [unchangeProfileDetails, setUnchangeProfileDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const getData = async () => {
    const arrayPromises = [];
    const headers = {
      headers: {
        Authorization: parseLocalStorage?.token,
      },
    };

    arrayPromises.push(
      axios
        .get(
          process.env.REACT_APP_BASE_URL + APIVariables.USERS_PROFILE,
          headers
        )
        .catch((error) => {
          handleCatch(error);
        })
    );
    arrayPromises.push(
      axios
        .get(
          process.env.REACT_APP_BASE_URL + APIVariables.PATIENT_STATISTICS,
          headers
        )
        .catch((error) => {
          handleCatch(error);
        })
    );

    let [profile, statistics] = await Promise.all(arrayPromises);

    setPatientsStatistics(statistics?.data);

    profile = getProfile(profile);

    setProfileDetails(profile);
    setUnchangeProfileDetails(profile);

    setIsLoading(false);
  };

  useEffect(() => {
    getData();

    // When destroy profile component, remove IS_EDIT_MODE_IRELAND  from localstorage
    return () => {
      localStorage.removeItem(LocalStorage.IS_EDIT_MODE_IRELAND);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProfile = (profile) => {
    let user;
    if (isPrescriber) {
      user = {
        isLoggedIn: true,
        token: parseLocalStorage.token,
        ...profile?.data,
        ...profile?.data?.prescriber,
        registration_number: profile?.data?.prescriber?.registration_number,
        hospital: profile?.data?.prescriber?.hospital?.id,
        job_title: profile?.data?.prescriber?.job_title,
      };
    } else if (isConnectedNurse || isConnectedRegistrar) {
      user = {
        isLoggedIn: true,
        token: parseLocalStorage.token,
        ...profile?.data,
        // ...(profile?.data?.user_type === registrationCategory.PRESCRIBER && {
        ...profile?.data?.prescriber,
        registration_number: profile?.data?.prescriber?.registration_number,
        hospital: profile?.data?.prescriber?.hospital?.id,
        job_title: profile?.data?.connected_nurse?.job_title,
        // }),
      };
    } else {
      user = {
        isLoggedIn: true,
        token: parseLocalStorage?.token,
        ...profile?.data,
        ...(profile?.data.user_type === registrationCategory.PHARMACY && {
          ...profile?.data.pharmacy,
          registration_number: profile?.data.pharmacy.registration_number,
          phone_number: displayPhoneNumberWithSpace(profile?.data.phone_number),
          job_title: profile?.data.user_type,
        }),
      };
    }

    return user;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filterValue = value;

    const nameRegex = /[^a-zA-Z -]/g;

    if (name === "first_name" || name === "last_name") {
      if (value === "" || nameRegex.test(e.target.value)) {
        filterValue = filterValue.replace(nameRegex, "");
      }
    }

    if (name === "phone_number") {
      const phoneRegex = /\D/g;
      if (value === "" || phoneRegex.test(e.target.value)) {
        filterValue = value.replace(phoneRegex, "");
      }

      filterValue = formatPhoneNumber(filterValue);

      setProfileDetails((prevState) => ({
        ...prevState,
        [name]: filterValue,
      }));
    }

    setProfileDetails((prevState) => ({
      ...prevState,
      [name]: filterValue,
    }));
  };

  const onClickEditSave = async () => {
    setIsSubmitting(false);
    if (!isEditMode) {
      setIsEditMode(true);
      localStorage.setItem(LocalStorage.IS_EDIT_MODE_IRELAND, true);
    }
    if (isEditMode) {
      setIsSubmitting(true);
      localStorage.removeItem(LocalStorage.IS_EDIT_MODE_IRELAND);
      if (areAllFields) {
        let buildEditObject = {};

        const prescriber = {
          ...(unchangeProfileDetails.job_title !== profileDetails.job_title && {
            job_title: profileDetails.job_title,
          }),
          ...(unchangeProfileDetails?.pharmacy?.user_type !==
            profileDetails.job_title && {
            job_title: profileDetails.job_title,
          }),
        };

        buildEditObject = {
          ...buildEditObject,
          ...(unchangeProfileDetails.first_name !==
            profileDetails.first_name && {
            first_name: profileDetails.first_name,
          }),
          ...(unchangeProfileDetails.last_name !== profileDetails.last_name && {
            last_name: profileDetails.last_name,
          }),
          ...(unchangeProfileDetails.phone_number !==
            profileDetails.phone_number && {
            phone_number: profileDetails.phone_number,
          }),
          ...(unchangeProfileDetails.email !== profileDetails.email && {
            email: profileDetails.email,
          }),
          ...(!isEmptyObject(prescriber) && {
            prescriber: prescriber,
          }),
        };

        if (!isEmptyObject(buildEditObject)) {
          buildEditObject = {
            ...buildEditObject,
            first_name: profileDetails.first_name,
            last_name: profileDetails.last_name,
            job_title: profileDetails.job_title,
            ...((isPrescriber || isConnectedNurse || isConnectedRegistrar) && {
              prescriber: {
                ...buildEditObject.prescriber,
                job_title: profileDetails.job_title,
              },
            }),
            ...(isPharmacy && {
              pharmacyuser: {
                ...buildEditObject.prescriber,
              },
            }),
          };

          buildEditObject.phone_number = buildEditObject?.phone_number
            ?.replace("+", "")
            .replace(/ /g, "");

          let res;

          const conditionalCall = isPrescriber
            ? APIVariables.USERS_PROFILE_PRESCRIBER_EDIT
            : isPharmacy
            ? APIVariables.USERS_PROFILE_PHARMACY_EDIT
            : APIVariables.USERS_PROFILE_NURSE_EDIT;
          res = await axios
            .put(
              process.env.REACT_APP_BASE_URL + conditionalCall,
              buildEditObject,
              {
                headers: {
                  Authorization: parseLocalStorage.token,
                },
              }
            )
            .catch(function (error) {
              handleCatch(error);

              const errors = error?.response?.data;
              const errorsArray = [];

              errorsArray.push(errors);
            });

          if (res?.status === 200) {
            setUnchangeProfileDetails(profileDetails);

            updateFirstAndLastNameFromLocalStorage(profileDetails);

            setLoggedInInitials(
              `${profileDetails.first_name?.charAt(
                0
              )}${profileDetails.last_name?.charAt(0)}`
            );

            setIsEditMode(false);
          }
        } else {
          setIsEditMode(false);
        }
      }
    }
  };

  const updateFirstAndLastNameFromLocalStorage = () => {
    const storage = localStorage.getItem(LocalStorage.USER_IRELAND);
    let parseStorage = JSON.parse(storage);

    parseStorage = {
      ...parseStorage,
      first_name: profileDetails.first_name,
      last_name: profileDetails.last_name,
    };

    localStorage.setItem(
      LocalStorage.USER_IRELAND,
      JSON.stringify(parseStorage)
    );
  };

  const validateForm = ({
    job_title = "",
    phone_number = "",
    first_name = "",
    last_name = "",
    email = "",
  }) => ({
    job_title: {
      isValid: job_title.trim().length > 0,
    },
    phone_number: {
      isValid: isPharmacy ? phone_number.trim().length > 0 : true,
    },
    phone_number_validate: {
      isValid: isPharmacy
        ? validatePhoneNumber(phone_number.replace(/ /g, ""))
        : true,
    },
    first_name: {
      isValid: first_name.trim().length > 0,
    },
    last_name: {
      isValid: last_name.trim().length > 0,
    },
    email: {
      isValid: email.trim().length > 0,
    },
    emailValid: {
      isValid: validateEmail(email),
    },
  });

  const { job_title, phone_number, first_name, last_name, email } =
    profileDetails;

  const validateScreenFields = validateForm({
    first_name,
    last_name,
    job_title,
    phone_number,
    email,
  });

  const areAllFields = Object.values(validateScreenFields).every((field) => {
    return field.isValid;
  });

  const areChanges = () => {
    return unchangeProfileDetails !== profileDetails;
  };

  const setUndoChanges = () => {
    setProfileDetails(unchangeProfileDetails);
    setIsEditMode(false);
  };

  return (
    <StyledProfile>
      {isLoading ? (
        <Loader />
      ) : (
        <React.Fragment>
          <PatientsDetails patientsDetails={patientsStatistics} />
          <div className="layout-modal mt-4">
            <div className="wrapper-header">
              <h1 className="select-category-txt">
                {isPrescriber
                  ? `Dr. ${
                      profileDetails.first_name + " " + profileDetails.last_name
                    }`
                  : profileDetails.first_name + " " + profileDetails.last_name}
              </h1>
              <div className="wrapper-header">
                {areChanges() && (
                  <button
                    className="undo-changes font-size-18"
                    onClick={() => setUndoChanges()}
                  >
                    <Undo /> Undo Changes
                  </button>
                )}

                <button className="second-button" onClick={onClickEditSave}>
                  {!isEditMode ? "Edit Profile" : "Save Edits"} <Pen />
                </button>
              </div>
            </div>

            <hr className="green-hr" />

            <h2 className="title-details mt-5">Personal Details</h2>

            <div
              className={"container min-height " + (!isEditMode ? "sign" : "")}
            >
              <div className="row">
                <div className="col">
                  <label
                    className={
                      isConnectedNurse || isPrescriber || isConnectedRegistrar
                        ? "label-disabled"
                        : !isEditMode
                        ? "label-disabled"
                        : isSubmitting &&
                          !validateScreenFields.first_name.isValid
                        ? "error-text-color"
                        : ""
                    }
                  >
                    {isPrescriber
                      ? "Physician First Name*"
                      : isPharmacy
                      ? "Pharmacist First Name*"
                      : "First Name*"}
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={profileDetails.first_name}
                    disabled={isPrescriber ? true : (!isEditMode || isConnectedNurse || isConnectedRegistrar)}
                    onChange={handleChange}
                    className={
                      isConnectedNurse || isPrescriber || isConnectedRegistrar
                        ? "label-disabled"
                        : !isEditMode
                        ? "disabled"
                        : isSubmitting &&
                          !validateScreenFields.first_name.isValid
                        ? "error"
                        : "text-activated"
                    }
                    maxLength={30}
                  />
                  {isSubmitting &&
                    !validateScreenFields.first_name?.isValid && (
                      <span className="required-text">Required</span>
                    )}
                </div>
                <div className="col">
                  <label
                    className={
                      isConnectedNurse || isConnectedRegistrar
                        ? "label-disabled"
                        : !isEditMode
                        ? "label-disabled"
                        : isSubmitting &&
                          !validateScreenFields.last_name.isValid
                        ? "error-text-color"
                        : ""
                    }
                  >
                    {isPrescriber
                      ? "Physician Last Name*"
                      : isPharmacy
                      ? "Pharmacist Last Name*"
                      : "Last Name*"}
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={profileDetails.last_name}
                    disabled={!isEditMode || isConnectedNurse || isConnectedRegistrar}
                    onChange={handleChange}
                    className={
                      isConnectedNurse || isConnectedRegistrar
                        ? "label-disabled"
                        : !isEditMode
                        ? "disabled"
                        : isSubmitting &&
                          !validateScreenFields.last_name.isValid
                        ? "error"
                        : "text-activated"
                    }
                    maxLength={30}
                  />
                  {isSubmitting && !validateScreenFields.last_name?.isValid && (
                    <span className="required-text">Required</span>
                  )}
                </div>
              </div>
              {!(isConnectedNurse || isConnectedRegistrar) ? (
                <div className="row">
                  <div className="col">
                    <label className="label-disabled">
                      {isPrescriber
                        ? "Medical Council Number*"
                        : isPharmacy
                        ? "Pharmacy GMS Number*"
                        : "Registration Number*"}
                    </label>
                    <input
                      type="text"
                      name="registration_number"
                      value={profileDetails.registration_number ?? ""}
                      disabled={true}
                      onChange={handleChange}
                      className="disabled"
                    />
                  </div>

                  <div className="col">
                    <label
                      className={
                        !isEditMode || isPharmacy
                          ? "label-disabled"
                          : isSubmitting &&
                            !validateScreenFields.job_title.isValid
                          ? "error-text-color"
                          : ""
                      }
                    >
                      Job Title*
                    </label>
                    <input
                      type="text"
                      name="job_title"
                      maxLength={50}
                      value={
                        profileDetails.job_title ??
                        profileDetails?.connected_nurse?.job_title ??
                        ""
                      }
                      disabled={isPharmacy ? true : !isEditMode}
                      onChange={handleChange}
                      className={
                        !isEditMode || isPharmacy
                          ? "disabled"
                          : isSubmitting &&
                            !validateScreenFields.job_title.isValid
                          ? "error"
                          : "text-activated"
                      }
                    />
                    {isSubmitting &&
                      !validateScreenFields.job_title?.isValid && (
                        <span className="required-text">Required</span>
                      )}
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col">
                    <label
                      className={
                        !isEditMode || isPharmacy
                          ? "label-disabled"
                          : isSubmitting &&
                            !validateScreenFields.job_title.isValid
                          ? "error-text-color"
                          : ""
                      }
                    >
                      Job Title*
                    </label>
                    <input
                      type="text"
                      name="job_title"
                      value={
                        profileDetails.job_title ??
                        profileDetails?.connected_nurse?.job_title ??
                        ""
                      }
                      disabled={isPharmacy ? true : !isEditMode}
                      onChange={handleChange}
                      className={
                        !isEditMode || isPharmacy
                          ? "disabled"
                          : isSubmitting &&
                            !validateScreenFields.job_title.isValid
                          ? "error"
                          : "text-activated"
                      }
                      maxLength={50}
                    />
                    {isSubmitting &&
                      !validateScreenFields.job_title?.isValid && (
                        <span className="required-text">Required</span>
                      )}
                  </div>
                  <div className="col"></div>
                </div>
              )}

              {isPharmacy && (
                <div className="row">
                  <div className="col">
                    <label className="label-disabled">Pharmacy Name</label>
                    <input
                      type="text"
                      name="pharmacyName"
                      value={profileDetails?.pharmacy?.name ?? ""}
                      disabled={true}
                      onChange={handleChange}
                      className="disabled"
                    />
                  </div>
                  <div className="col">
                    <label className="label-disabled">Pharmacy Address</label>
                    <input
                      type="text"
                      name="pharmacyAddress"
                      value={
                        profileDetails?.pharmacy?.address?.full_address ?? ""
                      }
                      disabled={true}
                      onChange={handleChange}
                      className="disabled"
                    />
                  </div>
                </div>
              )}
              {!isPharmacy && (
                <div className="row">
                  <div className="col">
                    <label className="label-disabled">Hospital Name:</label>
                    <input
                      type="text"
                      name="hospital_name"
                      value={
                        profileDetails?.prescriber?.hospital?.name ??
                        profileDetails?.connected_nurse?.prescribers[0]
                          ?.hospital?.name
                      }
                      disabled
                    />
                  </div>

                  <div className="col">
                    <label className="label-disabled">Address*</label>
                    <input
                      type="text"
                      name="hospital_address"
                      value={
                        profileDetails?.prescriber?.hospital?.address
                          .full_address ??
                        profileDetails?.connected_nurse?.prescribers[0]
                          ?.hospital?.address?.full_address
                      }
                      disabled
                    />
                  </div>
                </div>
              )}

              <div className="row">
                {!isPharmacy ? (
                  <div className="col">
                    <label
                      className={
                        isConnectedNurse || isPrescriber || isConnectedRegistrar
                          ? "label-disabled"
                          : !isEditMode
                          ? "label-disabled"
                          : (isSubmitting &&
                              !validateScreenFields.email.isValid) ||
                            (isSubmitting &&
                            !validateScreenFields.emailValid.isValid &&
                            validateScreenFields.email.isValid
                              ? "error-text-color"
                              : "")
                      }
                    >
                      Email*
                    </label>
                    <input
                      type="text"
                      name="email"
                      value={profileDetails.email ?? ""}
                      disabled
                      onChange={handleChange}
                      className={
                        isConnectedNurse || isPrescriber || isConnectedRegistrar
                          ? "label-disabled"
                          : !isEditMode
                          ? "disabled"
                          : (isSubmitting &&
                              !validateScreenFields.email.isValid) ||
                            (isSubmitting &&
                              !validateScreenFields.emailValid.isValid &&
                              validateScreenFields.email.isValid)
                          ? "error"
                          : "text-activated"
                      }
                    />
                    {isSubmitting && !validateScreenFields.email?.isValid && (
                      <span className="required-text">Required</span>
                    )}
                    {isSubmitting &&
                      !validateScreenFields.emailValid.isValid &&
                      validateScreenFields.email.isValid && (
                        <span className="required-text">Invalid Email</span>
                      )}
                  </div>
                ) : (
                  <div className="col"></div>
                )}
                <div className="col" />
              </div>
              {isPharmacy && (
                <div className="row">
                  <div className="col">
                    <label
                      className={
                        !isEditMode
                          ? "label-disabled"
                          : isSubmitting &&
                            (!validateScreenFields.phone_number.isValid ||
                              !validateScreenFields?.phone_number_validate
                                ?.isValid)
                          ? "error-text-color"
                          : ""
                      }
                    >
                      Pharmacy Contact Number:*
                    </label>
                    <input
                      type="text"
                      name="phone_number"
                      value={profileDetails.phone_number ?? ""}
                      disabled={!isEditMode}
                      onChange={handleChange}
                      className={
                        !isEditMode
                          ? "disabled"
                          : isSubmitting &&
                            (!validateScreenFields.phone_number.isValid ||
                              !validateScreenFields?.phone_number_validate
                                ?.isValid)
                          ? "error"
                          : "text-activated"
                      }
                      maxLength={13}
                    />
                    {isSubmitting &&
                    !validateScreenFields.phone_number?.isValid ? (
                      <span className="required-text">Required</span>
                    ) : isSubmitting &&
                      !validateScreenFields?.phone_number_validate?.isValid ? (
                      <span className="required-text">
                        Invalid Phone Number
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                  <div className="col">
                    <label
                      className={
                        !isEditMode || isPharmacy
                          ? "label-disabled"
                          : isSubmitting && !validateScreenFields.email.isValid
                          ? "error-text-color"
                          : ""
                      }
                    >
                      Email:*
                    </label>
                    {/*TO DO: Modify input's value below when we get the API*/}
                    <input
                      type="text"
                      name="email"
                      value={profileDetails.email ?? ""}
                      disabled={isPharmacy ? true : !isEditMode}
                      onChange={handleChange}
                      className={
                        !isEditMode || isPharmacy
                          ? "disabled"
                          : isSubmitting && !validateScreenFields.email.isValid
                          ? "error"
                          : "text-activated"
                      }
                    />
                    {isSubmitting && !validateScreenFields.email?.isValid && (
                      <span className="required-text">Required</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </React.Fragment>
      )}
    </StyledProfile>
  );
};
export default Profile;
